import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, Row, Col, Dropdown, Nav, Tab } from 'react-bootstrap';
import { theme } from '../../theme/theme.js';
import './css/DetailView.css';
import axios from 'axios';
import * as constants from '../../constants/CONSTANT';
import { crmAuthHeaders } from '../../api/DataApi';
import EditLayoutModal from './EditLayoutModal';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import DataApi from '../../api/DataApi';
import TaskActivityTimeline from './TaskActivityTimeline';
import EmailSender from '../common/EmailSender';
import ScoreDisplay from '../common/ScoreDisplay';
import SearchWidget from './SearchWidget';
import SearchViewRecordsTable from './SearchViewRecordsTable';
import Attachment from '../Attachment';
import FilePreviewModal from '../common/FilePreviewModal';
import jwt_decode from 'jwt-decode';
import {
  formatFieldValue as formatFieldValueUtil,
  formatCurrency,
  formatNumber,
  formatDate,
  formatDateTime,
  formatTime,
  formatCalculatedValue,
  normalizeCalculatedDisplayConfig
} from '../../utils/formatUtils';

// ─────────────────────────────────────────────────────────────────────────────
// Google Maps JS API loader (singleton)
//   - Fetches the public Maps key from /api/geo/config (server reads
//     GOOGLE_MAPS_API_KEY env), then injects the script tag exactly once.
//   - All map widgets share the same load promise.
// ─────────────────────────────────────────────────────────────────────────────
let _googleMapsLoadPromise = null;

function loadGoogleMapsApi() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window is not available'));
  }
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google);
  }
  if (_googleMapsLoadPromise) return _googleMapsLoadPromise;

  _googleMapsLoadPromise = (async () => {
    const resp = await axios.get(`${constants.API_BASE_URL}/api/geo/config`, {
      headers: { ...crmAuthHeaders() }
    });
    const data = resp?.data?.data || {};
    if (!data.hasKey || !data.apiKey) {
      throw new Error('Google Maps API key is not configured on the server (GOOGLE_MAPS_API_KEY).');
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-loader="ibs-google-maps"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.google));
        existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
        return;
      }
      const cbName = `__ibsGoogleMapsReady_${Date.now()}`;
      window[cbName] = () => {
        try { delete window[cbName]; } catch (_) { /* ignore */ }
        resolve(window.google);
      };
      const script = document.createElement('script');
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(data.apiKey)}` +
        `&libraries=places&callback=${cbName}`;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-loader', 'ibs-google-maps');
      script.onerror = () => reject(new Error('Failed to load Google Maps'));
      document.body.appendChild(script);
    });
  })();

  // If the load fails, allow a retry on the next render.
  _googleMapsLoadPromise.catch(() => { _googleMapsLoadPromise = null; });

  return _googleMapsLoadPromise;
}

/**
 * Extract a primitive value from a record field that might be:
 *   - a primitive (string/number)
 *   - an enriched object: { value, displayValue, isReference, ... }
 */
function extractFieldPrimitive(fieldData) {
  if (fieldData === null || fieldData === undefined) return null;
  if (typeof fieldData !== 'object' || Array.isArray(fieldData)) return fieldData;
  if (fieldData.value !== undefined && fieldData.value !== null) return fieldData.value;
  if (fieldData.displayValue !== undefined && fieldData.displayValue !== null) return fieldData.displayValue;
  return null;
}

/**
 * Read a field value from the detail-layout `recordData` shape used in the app:
 *   {
 *     highlightSection: [{ fieldName, value, ... }],
 *     details: [{ fields: [{ fieldName, value, ... }] }],
 *     otherHiddenFields: [{ fieldName, value, ... }]
 *   }
 *
 * Falls back to a flat `{ field_name: value }` shape if that's what we get.
 */
function readFieldFromRecord(recordData, fieldName) {
  if (!recordData || !fieldName) return null;
  const want = String(fieldName).toLowerCase();

  const fromList = (list) => {
    if (!Array.isArray(list)) return undefined;
    const match = list.find((f) => f && String(f.fieldName || '').toLowerCase() === want);
    if (!match) return undefined;
    // Prefer explicit value, then displayValue
    if (match.value !== undefined && match.value !== null) return extractFieldPrimitive(match.value);
    if (match.displayValue !== undefined && match.displayValue !== null) {
      return extractFieldPrimitive(match.displayValue);
    }
    return null;
  };

  // 1) highlightSection
  let v = fromList(recordData.highlightSection);
  if (v !== undefined) return v;

  // 2) details[].fields[]
  const detailFields = Array.isArray(recordData.details)
    ? recordData.details.flatMap((s) => (s && Array.isArray(s.fields) ? s.fields : []))
    : [];
  v = fromList(detailFields);
  if (v !== undefined) return v;

  // 3) otherHiddenFields
  v = fromList(recordData.otherHiddenFields);
  if (v !== undefined) return v;

  // 4) Flat object fallback (some callers may pass `{ field_name: value }`)
  if (Object.prototype.hasOwnProperty.call(recordData, fieldName)) {
    return extractFieldPrimitive(recordData[fieldName]);
  }
  const key = Object.keys(recordData).find((k) => k.toLowerCase() === want);
  if (key !== undefined) return extractFieldPrimitive(recordData[key]);

  return null;
}

function parseCoordinate(raw) {
  if (raw === null || raw === undefined || raw === '') return NaN;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) ? n : NaN;
}

function parseLatLngCombined(raw) {
  if (raw === null || raw === undefined) return { lat: NaN, lng: NaN };
  const parts = String(raw).split(',').map((s) => s.trim()).filter((s) => s !== '');
  if (parts.length < 2) return { lat: NaN, lng: NaN };
  return { lat: parseCoordinate(parts[0]), lng: parseCoordinate(parts[1]) };
}

/**
 * Read a value from a related-list record returned by the widget-data API.
 *
 * `fetchRelatedRecordsForConfig` returns records in the shape:
 *   { id, rowIndex, fields: [{ fieldName, value, displayValue, type, ... }, ...] }
 *
 * Some older code paths may return flat `{ field_name: value }`; this helper
 * handles both for safety.
 */
function readFieldFromFlatRecord(record, fieldName) {
  if (!record || !fieldName) return null;
  const want = String(fieldName).toLowerCase();

  // 1) Preferred shape: record.fields is an array of { fieldName, value, displayValue }
  if (Array.isArray(record.fields)) {
    const match = record.fields.find(
      (f) => f && String(f.fieldName || '').toLowerCase() === want
    );
    if (match) {
      if (match.value !== undefined && match.value !== null) {
        return extractFieldPrimitive(match.value);
      }
      if (match.displayValue !== undefined && match.displayValue !== null) {
        return extractFieldPrimitive(match.displayValue);
      }
      return null;
    }
  }

  // 2) Flat fallback: { field_name: value | { value, displayValue } }
  const direct = record[fieldName];
  if (direct !== undefined) return extractFieldPrimitive(direct);
  const key = Object.keys(record).find((k) => k.toLowerCase() === want);
  if (key !== undefined) return extractFieldPrimitive(record[key]);
  return null;
}

/**
 * Resolve coordinates from a single route record using the route config.
 * Same return contract as resolveMapCoords (returns { lat, lng } or { address }).
 */
function resolveRouteRecordCoords(record, routeConfig) {
  const mode = (routeConfig && routeConfig.coord_source_mode) || 'split';
  if (mode === 'split') {
    const lat = parseCoordinate(readFieldFromFlatRecord(record, routeConfig.lat_field));
    const lng = parseCoordinate(readFieldFromFlatRecord(record, routeConfig.lng_field));
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return { error: 'invalid-coords' };
  }
  if (mode === 'combined') {
    const raw = readFieldFromFlatRecord(record, routeConfig.latlng_field);
    const { lat, lng } = parseLatLngCombined(raw);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return { error: 'invalid-coords' };
  }
  if (mode === 'address') {
    const fields = Array.isArray(routeConfig.address_fields) ? routeConfig.address_fields : [];
    const parts = [];
    for (const fn of fields) {
      const v = readFieldFromFlatRecord(record, fn);
      if (v !== null && v !== undefined && String(v).trim() !== '') parts.push(String(v).trim());
    }
    const address = parts.join(', ');
    if (!address) return { error: 'no-address' };
    return { address };
  }
  return { error: `unsupported-mode:${mode}` };
}

/**
 * Resolve coordinates from a record using the map widget config.
 * Returns { lat, lng } when split/combined succeed; for 'address' mode, returns
 * { address } so the caller can geocode it (client-side via Maps Geocoder).
 */
/**
 * Great-circle distance between two {lat,lng} points in km (Haversine).
 */
function haversineDistanceKm(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Nearest-neighbor reorder of stops starting from `origin`. Returns a new
 * array; `origin` itself is NOT included. Best-effort (TSP heuristic).
 */
function reorderByNearestNeighbor(origin, stops) {
  if (!origin || !Array.isArray(stops) || stops.length < 2) return stops || [];
  const remaining = [...stops];
  const ordered = [];
  let cur = { lat: origin.lat, lng: origin.lng };
  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineDistanceKm(cur, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push(next);
    cur = { lat: next.lat, lng: next.lng };
  }
  return ordered;
}

function resolveMapCoords(recordData, config) {
  const mode = (config && config.coord_source_mode) || 'split';
  if (mode === 'split') {
    const lat = parseCoordinate(readFieldFromRecord(recordData, config.lat_field));
    const lng = parseCoordinate(readFieldFromRecord(recordData, config.lng_field));
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return { error: 'Latitude/Longitude fields are empty or invalid on this record.' };
  }
  if (mode === 'combined') {
    const raw = readFieldFromRecord(recordData, config.latlng_field);
    const { lat, lng } = parseLatLngCombined(raw);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return { error: 'Combined "lat, lng" field is empty or invalid on this record.' };
  }
  if (mode === 'address') {
    const fields = Array.isArray(config.address_fields) ? config.address_fields : [];
    const parts = [];
    for (const fn of fields) {
      const v = readFieldFromRecord(recordData, fn);
      if (v !== null && v !== undefined && String(v).trim() !== '') parts.push(String(v).trim());
    }
    const address = parts.join(', ');
    if (!address) return { error: 'No address fields populated on this record.' };
    return { address };
  }
  return { error: `Unsupported map coord_source_mode: ${mode}` };
}

const MapWidgetView = ({ widget }) => {
  const config = useMemo(() => widget?.config || {}, [widget?.config]);
  const recordData = widget?.recordData || null;
  const dataSource = config.data_source || 'record';
  const isRoute = dataSource === 'route';
  // For route mode we read from widget.data.records (populated by the existing
  // widget-data API via the related-list pipeline).
  const widgetData = widget?.data || null;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [statusInfo, setStatusInfo] = useState(''); // e.g. "N stops"
  // Route-mode start location: { lat, lng, label, source: 'current'|'address'|'manual' }
  const [startLocation, setStartLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState('idle'); // 'idle'|'detecting'|'denied'|'unavailable'|'ready'
  const [startAddrInput, setStartAddrInput] = useState('');
  const [showStartAddrInput, setShowStartAddrInput] = useState(false);
  const [geocodingStart, setGeocodingStart] = useState(false);
  const [showGeoHelp, setShowGeoHelp] = useState(false);
  // Route summary (rendered as a list below the map)
  const [routeStops, setRouteStops] = useState([]);
  const [totalDistanceKm, setTotalDistanceKm] = useState(0);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);            // record-mode single marker
  const infoWindowRef = useRef(null);        // record-mode info window
  const routeMarkersRef = useRef([]);        // route-mode markers
  const routePolylineRef = useRef(null);     // route-mode polyline
  const directionsRendererRef = useRef(null); // route-mode directions overlay
  const startMarkerRef = useRef(null);       // route-mode start-location marker
  const triedAutoGeoRef = useRef(false);     // ensure auto-geo runs only once per widget

  const maxHeight = Number.isFinite(parseInt(config.max_height, 10))
    ? parseInt(config.max_height, 10)
    : 400;
  const zoom = Number.isFinite(parseInt(config.zoom, 10)) ? parseInt(config.zoom, 10) : 14;

  // Resolve coordinates from this record's data (record-mode only)
  const resolved = useMemo(
    () => (isRoute ? null : resolveMapCoords(recordData, config)),
    [recordData, config, isRoute]
  );

  const escapeHtml = (s) => String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  // Clear any route-mode overlays from a prior render
  const clearRouteOverlays = () => {
    if (Array.isArray(routeMarkersRef.current)) {
      routeMarkersRef.current.forEach((m) => { try { m.setMap(null); } catch (_) { /* */ } });
    }
    routeMarkersRef.current = [];
    if (routePolylineRef.current) {
      try { routePolylineRef.current.setMap(null); } catch (_) { /* */ }
      routePolylineRef.current = null;
    }
    if (directionsRendererRef.current) {
      try { directionsRendererRef.current.setMap(null); } catch (_) { /* */ }
      directionsRendererRef.current = null;
    }
    if (startMarkerRef.current) {
      try { startMarkerRef.current.setMap(null); } catch (_) { /* */ }
      startMarkerRef.current = null;
    }
  };

  // ---- Start-location handlers (route mode only) ---------------------------
  const detectCurrentLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }
    // Geolocation is only allowed in secure contexts (HTTPS or localhost).
    // On plain HTTP the browser silently refuses, which feels like "denied".
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setGeoStatus('insecure');
      return;
    }
    setGeoStatus('detecting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos?.coords?.latitude;
        const lng = pos?.coords?.longitude;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setStartLocation({ lat, lng, label: 'My current location', source: 'current' });
          setGeoStatus('ready');
        } else {
          setGeoStatus('unavailable');
        }
      },
      (err) => {
        // err.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (err && err.code === 1) setGeoStatus('denied');
        else if (err && err.code === 3) setGeoStatus('timeout');
        else setGeoStatus('unavailable');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  const geocodeStartAddress = async () => {
    const addr = (startAddrInput || '').trim();
    if (!addr) return;
    setGeocodingStart(true);
    try {
      const google = await loadGoogleMapsApi();
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: addr }, (results, st) => {
        setGeocodingStart(false);
        if (st === 'OK' && results && results.length) {
          const loc = results[0].geometry?.location;
          if (loc) {
            setStartLocation({
              lat: loc.lat(),
              lng: loc.lng(),
              label: results[0].formatted_address || addr,
              source: 'address'
            });
            setShowStartAddrInput(false);
            setStartAddrInput('');
          }
        }
      });
    } catch (_) {
      setGeocodingStart(false);
    }
  };

  const clearStartLocation = () => {
    setStartLocation(null);
    setGeoStatus('idle');
  };

  // Auto-detect current location once when the route widget first becomes
  // visible (after data is ready). Users can override via the toolbar.
  useEffect(() => {
    if (!isRoute) return;
    if (triedAutoGeoRef.current) return;
    if (isCollapsed) return;
    if (!widgetData) return;
    triedAutoGeoRef.current = true;
    detectCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoute, isCollapsed, widgetData]);

  useEffect(() => {
    let cancelled = false;

    async function renderRoute(google) {
      const route = config.route || {};
      const records = Array.isArray(widgetData?.records) ? widgetData.records : [];

      // eslint-disable-next-line no-console
      console.group(`[MapWidget/Route] ${widget?.widgetLabel || 'Map'} (widgetId=${widget?.id ?? '?'})`);
      // eslint-disable-next-line no-console
      console.log('route config:', route);
      // eslint-disable-next-line no-console
      console.log('records received:', records.length, records);

      if (records.length === 0) {
        // eslint-disable-next-line no-console
        console.warn('[MapWidget/Route] No records returned by widget-data API. ' +
          'Check that (a) the related list id is correct, (b) the parent record has children, ' +
          'and (c) the user has read permission on the child object.');
        // eslint-disable-next-line no-console
        console.groupEnd();
        setStatus('error');
        setErrorMsg('No child records found for this route.');
        return;
      }

      // Show what each record's keys actually look like — this is the most
      // common reason coords fail to resolve (field name mismatch / nested objects).
      // eslint-disable-next-line no-console
      console.log('[MapWidget/Route] first record sample keys:', Object.keys(records[0] || {}));
      // eslint-disable-next-line no-console
      console.log('[MapWidget/Route] first record raw:', records[0]);

      // 1) Resolve coordinates per record (geocoding addresses if needed).
      const stops = [];
      const skipped = [];
      const geocoder = new google.maps.Geocoder();
      for (let i = 0; i < records.length; i++) {
        const rec = records[i];
        const r = resolveRouteRecordCoords(rec, route);

        const labelText = route.label_field
          ? readFieldFromFlatRecord(rec, route.label_field)
          : null;
        const stop = {
          index: i + 1,
          label: labelText ? String(labelText) : `Stop ${i + 1}`,
          lat: NaN,
          lng: NaN,
          formattedAddress: null,
          recordId: extractFieldPrimitive(rec.id) || null
        };

        // Per-record trace: shows exactly which fields were read and what came back.
        const traceFields = {};
        const mode = (route.coord_source_mode || 'split').toLowerCase();
        if (mode === 'split') {
          traceFields[route.lat_field] = readFieldFromFlatRecord(rec, route.lat_field);
          traceFields[route.lng_field] = readFieldFromFlatRecord(rec, route.lng_field);
        } else if (mode === 'combined') {
          traceFields[route.latlng_field] = readFieldFromFlatRecord(rec, route.latlng_field);
        } else if (mode === 'address') {
          (route.address_fields || []).forEach((fn) => {
            traceFields[fn] = readFieldFromFlatRecord(rec, fn);
          });
        }
        // eslint-disable-next-line no-console
        console.log(`[MapWidget/Route] record #${i + 1} id=${stop.recordId}`, {
          mode,
          readFields: traceFields,
          resolved: r
        });

        if (r.error) {
          // eslint-disable-next-line no-console
          console.warn(`[MapWidget/Route] record #${i + 1} skipped: ${r.error}`, { record: rec, route });
          skipped.push({ index: i + 1, reason: r.error });
          continue;
        }
        if (Number.isFinite(r.lat) && Number.isFinite(r.lng)) {
          stop.lat = r.lat;
          stop.lng = r.lng;
        } else if (r.address) {
          try {
            // Client-side geocoding (same approach used by record-mode).
            // Sequential to avoid hammering Google with a burst on every render.
            // eslint-disable-next-line no-await-in-loop
            const geo = await new Promise((resolve, reject) => {
              geocoder.geocode({ address: r.address }, (results, st) => {
                if (st === 'OK' && results && results.length) {
                  const loc = results[0].geometry.location;
                  resolve({
                    lat: loc.lat(),
                    lng: loc.lng(),
                    formatted: results[0].formatted_address || null
                  });
                } else {
                  reject(new Error(`Geocoding [${st}]`));
                }
              });
            });
            stop.lat = geo.lat;
            stop.lng = geo.lng;
            stop.formattedAddress = geo.formatted || r.address;
            // eslint-disable-next-line no-console
            console.log(`[MapWidget/Route] record #${i + 1} geocoded "${r.address}" -> ${stop.lat},${stop.lng}`);
          } catch (gErr) {
            // eslint-disable-next-line no-console
            console.warn(`[MapWidget/Route] record #${i + 1} geocode failed for "${r.address}":`, gErr?.message || gErr);
            skipped.push({ index: i + 1, reason: 'geocode-failed', address: r.address });
            continue;
          }
        }
        if (Number.isFinite(stop.lat) && Number.isFinite(stop.lng)) {
          stops.push(stop);
        } else {
          // eslint-disable-next-line no-console
          console.warn(`[MapWidget/Route] record #${i + 1} produced no usable coords`, { resolved: r });
          skipped.push({ index: i + 1, reason: 'no-coords' });
        }
        if (cancelled) return;
      }

      // eslint-disable-next-line no-console
      console.log(`[MapWidget/Route] summary: ${stops.length} stops, ${skipped.length} skipped`);
      if (skipped.length) {
        // eslint-disable-next-line no-console
        console.warn('[MapWidget/Route] skipped details:', skipped);
      }
      // eslint-disable-next-line no-console
      console.groupEnd();

      if (stops.length === 0) {
        setStatus('error');
        setErrorMsg(
          `Could not resolve coordinates for any child record. ` +
          `Check the browser console (look for "[MapWidget/Route]") to see which fields were read and why each record was skipped.`
        );
        setRouteStops([]);
        setTotalDistanceKm(0);
        return;
      }

      // 1.5) If a start location is set, reorder stops by nearest-neighbor
      //      from the start and renumber them so marker labels reflect the
      //      visit sequence (closest first). Always compute per-leg distances
      //      so the list below the map can show them.
      let orderedStops = stops;
      if (startLocation && Number.isFinite(startLocation.lat) && Number.isFinite(startLocation.lng)) {
        orderedStops = reorderByNearestNeighbor(
          { lat: startLocation.lat, lng: startLocation.lng },
          stops
        );
        orderedStops.forEach((s, i) => { s.index = i + 1; });
        // eslint-disable-next-line no-console
        console.log('[MapWidget/Route] reordered by nearest-neighbor from start:', startLocation, orderedStops);
      }

      // Per-leg distances (km). When a start is set, leg 1 = start→stop1; else
      // leg 1 is undefined (no "previous").
      orderedStops.forEach((s, i) => {
        if (i === 0) {
          s.distanceFromPrevKm = (startLocation && Number.isFinite(startLocation.lat))
            ? haversineDistanceKm(startLocation, s)
            : null;
        } else {
          s.distanceFromPrevKm = haversineDistanceKm(orderedStops[i - 1], s);
        }
      });
      const totalKm = orderedStops.reduce(
        (acc, s) => acc + (Number.isFinite(s.distanceFromPrevKm) ? s.distanceFromPrevKm : 0),
        0
      );

      // 2) (Re)create the map.
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapContainerRef.current, {
          center: { lat: orderedStops[0].lat, lng: orderedStops[0].lng },
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true
        });
      }

      // Wipe prior overlays + record-mode single marker
      if (markerRef.current) { try { markerRef.current.setMap(null); } catch (_) { /* */ } markerRef.current = null; }
      if (infoWindowRef.current) { try { infoWindowRef.current.close(); } catch (_) { /* */ } infoWindowRef.current = null; }
      clearRouteOverlays();

      // 3) Drop markers (numbered) + per-stop InfoWindow (address-first)
      const bounds = new google.maps.LatLngBounds();
      const sharedInfo = new google.maps.InfoWindow();
      infoWindowRef.current = sharedInfo;
      const popupGeocoder = new google.maps.Geocoder();
      // Pending reverse-geocode promises so concurrent clicks don't re-fire.
      const reverseGeoCache = new Map();
      const reverseGeocodePosition = (stop) => {
        if (stop.formattedAddress) return Promise.resolve(stop.formattedAddress);
        const key = `${stop.lat},${stop.lng}`;
        if (reverseGeoCache.has(key)) return reverseGeoCache.get(key);
        const p = new Promise((resolve) => {
          try {
            popupGeocoder.geocode(
              { location: { lat: stop.lat, lng: stop.lng } },
              (results, st) => {
                if (st === 'OK' && results && results.length && results[0].formatted_address) {
                  resolve(results[0].formatted_address);
                } else {
                  resolve(null);
                }
              }
            );
          } catch (_) { resolve(null); }
        });
        reverseGeoCache.set(key, p);
        return p;
      };

      const buildInfo = (stop, addressLine, loading) => {
        const lines = [
          `<div style="font-weight:600;margin-bottom:4px;">${escapeHtml(`${stop.index}. ${stop.label}`)}</div>`
        ];
        if (loading) {
          lines.push('<div style="color:#666;font-size:0.85em;font-style:italic;">Looking up address…</div>');
        } else if (addressLine) {
          lines.push(`<div>${escapeHtml(addressLine)}</div>`);
        } else {
          // Fall back to coords only when reverse-geocoding gave nothing.
          lines.push(`<div style="color:#555;font-size:0.85em;">${escapeHtml(`${stop.lat.toFixed(6)}, ${stop.lng.toFixed(6)}`)}</div>`);
        }
        return `<div style="max-width:280px;font-family:inherit;">${lines.join('')}</div>`;
      };

      orderedStops.forEach((s) => {
        const marker = new google.maps.Marker({
          position: { lat: s.lat, lng: s.lng },
          map: mapInstanceRef.current,
          label: { text: String(s.index), color: '#fff', fontSize: '12px', fontWeight: '600' },
          title: s.formattedAddress || s.label
        });
        marker.addListener('click', async () => {
          if (s.formattedAddress) {
            sharedInfo.setContent(buildInfo(s, s.formattedAddress, false));
          } else {
            // Show a loading state while we reverse-geocode the coords.
            sharedInfo.setContent(buildInfo(s, null, true));
          }
          sharedInfo.open({ map: mapInstanceRef.current, anchor: marker });

          if (!s.formattedAddress) {
            const addr = await reverseGeocodePosition(s);
            if (cancelled) return;
            if (addr) {
              s.formattedAddress = addr;
              marker.setTitle(addr);
              sharedInfo.setContent(buildInfo(s, addr, false));
            } else {
              // Reverse-geocoding gave nothing — fall back to coords.
              sharedInfo.setContent(buildInfo(s, null, false));
            }
          }
        });
        routeMarkersRef.current.push(marker);
        bounds.extend(marker.getPosition());
      });

      // 3.5) If a start location is set, drop a distinct green "S" marker
      //      and include it in the bounds so it's visible.
      const hasStart = !!(startLocation && Number.isFinite(startLocation.lat) && Number.isFinite(startLocation.lng));
      if (hasStart) {
        const startPos = { lat: startLocation.lat, lng: startLocation.lng };
        const startMarker = new google.maps.Marker({
          position: startPos,
          map: mapInstanceRef.current,
          label: { text: 'S', color: '#fff', fontSize: '12px', fontWeight: '700' },
          title: startLocation.label || 'Start',
          zIndex: 9999,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: '#15803d',
            strokeWeight: 2
          }
        });
        const startInfo = new google.maps.InfoWindow({
          content: `<div style="max-width:260px;font-family:inherit;">
            <div style="font-weight:600;margin-bottom:4px;">Start</div>
            <div>${escapeHtml(startLocation.label || '')}</div>
            <div style="color:#555;font-size:0.85em;margin-top:2px;">${escapeHtml(`${startLocation.lat.toFixed(6)}, ${startLocation.lng.toFixed(6)}`)}</div>
          </div>`
        });
        startMarker.addListener('click', () => {
          startInfo.open({ map: mapInstanceRef.current, anchor: startMarker });
        });
        startMarkerRef.current = startMarker;
        bounds.extend(startMarker.getPosition());
      }

      // Auto-fit, but cap zoom so a single tight cluster doesn't over-zoom
      if (orderedStops.length > 1 || hasStart) {
        mapInstanceRef.current.fitBounds(bounds, 48);
      } else {
        mapInstanceRef.current.setCenter({ lat: orderedStops[0].lat, lng: orderedStops[0].lng });
        mapInstanceRef.current.setZoom(zoom);
      }

      // 4) Connector — if a start is set, the path begins from the start.
      const connect = route.connect || 'polyline';
      const pathPoints = [
        ...(hasStart ? [{ lat: startLocation.lat, lng: startLocation.lng }] : []),
        ...orderedStops.map((s) => ({ lat: s.lat, lng: s.lng }))
      ];

      if (pathPoints.length >= 2 && connect === 'polyline') {
        routePolylineRef.current = new google.maps.Polyline({
          path: pathPoints,
          map: mapInstanceRef.current,
          strokeColor: '#1f6feb',
          strokeOpacity: 0.85,
          strokeWeight: 3
        });
      } else if (pathPoints.length >= 2 && connect === 'directions') {
        try {
          const ds = new google.maps.DirectionsService();
          const dr = new google.maps.DirectionsRenderer({
            map: mapInstanceRef.current,
            suppressMarkers: true, // keep our numbered markers
            preserveViewport: true,
            polylineOptions: { strokeColor: '#1f6feb', strokeOpacity: 0.85, strokeWeight: 4 }
          });
          directionsRendererRef.current = dr;
          const origin = pathPoints[0];
          const destination = pathPoints[pathPoints.length - 1];
          const waypoints = pathPoints.slice(1, -1).map((p) => ({
            location: { lat: p.lat, lng: p.lng },
            stopover: true
          }));
          ds.route(
            {
              origin,
              destination,
              waypoints,
              travelMode: google.maps.TravelMode.DRIVING
            },
            (result, st) => {
              if (cancelled) return;
              if (st === 'OK' && result) {
                dr.setDirections(result);
              } else {
                // Fallback to a straight polyline
                try { dr.setMap(null); } catch (_) { /* */ }
                directionsRendererRef.current = null;
                routePolylineRef.current = new google.maps.Polyline({
                  path: pathPoints,
                  map: mapInstanceRef.current,
                  strokeColor: '#1f6feb',
                  strokeOpacity: 0.85,
                  strokeWeight: 3
                });
              }
            }
          );
        } catch (_) { /* ignore: keep markers only */ }
      }

      setStatusInfo(
        `${orderedStops.length} stop${orderedStops.length === 1 ? '' : 's'}` +
        (hasStart ? ' — ordered from start' : '') +
        ` — ${totalKm.toFixed(1)} km total` +
        (skipped.length ? ` — ${skipped.length} skipped` : '')
      );

      // Publish the ordered stops + total to state so the summary list below
      // the map can render. We expose lightweight copies (no Google Map
      // objects) and let the list trigger reverse-geocoding for labels.
      setRouteStops(orderedStops.map((s) => ({
        index: s.index,
        label: s.label,
        lat: s.lat,
        lng: s.lng,
        formattedAddress: s.formattedAddress || null,
        distanceFromPrevKm: s.distanceFromPrevKm,
        recordId: s.recordId
      })));
      setTotalDistanceKm(totalKm);
      setStatus('ready');

      // Best-effort: reverse-geocode stops that don't yet have a human-
      // readable address so the list below the map shows real names instead
      // of just coordinates. Done in parallel; updates state once resolved.
      const toResolve = orderedStops.filter((s) => !s.formattedAddress);
      if (toResolve.length > 0) {
        const bgGeocoder = new google.maps.Geocoder();
        await Promise.all(toResolve.map((s) => new Promise((resolve) => {
          try {
            bgGeocoder.geocode(
              { location: { lat: s.lat, lng: s.lng } },
              (results, st) => {
                if (st === 'OK' && results && results.length && results[0].formatted_address) {
                  s.formattedAddress = results[0].formatted_address;
                }
                resolve();
              }
            );
          } catch (_) { resolve(); }
        })));
        if (cancelled) return;
        setRouteStops((prev) => prev.map((p) => {
          const fresh = orderedStops.find((o) => o.index === p.index);
          return fresh && fresh.formattedAddress
            ? { ...p, formattedAddress: fresh.formattedAddress }
            : p;
        }));
      }
    }

    async function renderSingle(google) {
      if (resolved.error) {
        setStatus('error');
        setErrorMsg(resolved.error);
        return;
      }

      let center = null;
      let formattedAddress = null;
      if (Number.isFinite(resolved.lat) && Number.isFinite(resolved.lng)) {
        center = { lat: resolved.lat, lng: resolved.lng };
      } else if (resolved.address) {
        try {
          const geocoder = new google.maps.Geocoder();
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: resolved.address }, (results, st) => {
              if (st === 'OK' && results && results.length) {
                const loc = results[0].geometry.location;
                resolve({
                  lat: loc.lat(),
                  lng: loc.lng(),
                  formatted: results[0].formatted_address || null
                });
              } else {
                reject(new Error(`Geocoding failed [${st}]: "${resolved.address}"`));
              }
            });
          });
          if (cancelled) return;
          center = { lat: result.lat, lng: result.lng };
          formattedAddress = result.formatted || resolved.address;
        } catch (err) {
          if (cancelled) return;
          setStatus('error');
          setErrorMsg(err?.message || 'Geocoding failed');
          return;
        }
      }

      if (!center) {
        setStatus('error');
        setErrorMsg('Could not resolve a location from this record.');
        return;
      }

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapContainerRef.current, {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true
        });
      } else {
        mapInstanceRef.current.setCenter(center);
        mapInstanceRef.current.setZoom(zoom);
      }

      // Wipe any route-mode overlays + prior single marker
      clearRouteOverlays();
      if (markerRef.current) { try { markerRef.current.setMap(null); } catch (_) { /* */ } }
      if (infoWindowRef.current) { try { infoWindowRef.current.close(); } catch (_) { /* */ } }

      const coordLabel = `${Number(center.lat).toFixed(6)}, ${Number(center.lng).toFixed(6)}`;
      const initialTitle = formattedAddress || resolved.address || coordLabel;
      const marker = new google.maps.Marker({
        position: center,
        map: mapInstanceRef.current,
        title: initialTitle
      });
      markerRef.current = marker;

      const buildInfoContent = (addressText) => {
        const lines = [];
        if (addressText) {
          lines.push(`<div style="font-weight:600;margin-bottom:4px;">${escapeHtml(addressText)}</div>`);
        }
        lines.push(`<div style="color:#555;font-size:0.85em;">${escapeHtml(coordLabel)}</div>`);
        return `<div style="max-width:260px;font-family:inherit;">${lines.join('')}</div>`;
      };

      const infoWindow = new google.maps.InfoWindow({
        content: buildInfoContent(formattedAddress)
      });
      infoWindowRef.current = infoWindow;

      marker.addListener('click', () => {
        infoWindow.open({ map: mapInstanceRef.current, anchor: marker });
      });

      if (!formattedAddress) {
        try {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: center }, (results, st) => {
            if (cancelled) return;
            if (st === 'OK' && results && results.length) {
              const addr = results[0].formatted_address;
              if (addr) {
                marker.setTitle(addr);
                infoWindow.setContent(buildInfoContent(addr));
              }
            }
          });
        } catch (_) { /* non-fatal */ }
      }

      setStatusInfo('');
      setStatus('ready');
    }

    async function render() {
      if (isCollapsed) return; // wait until expanded so the container has a size

      // Route mode: needs widget.data.records (the existing widget-data API
      // populates this when config.data_source === 'route').
      if (isRoute) {
        if (!widgetData) {
          // still loading; keep spinner
          setStatus('loading');
          setErrorMsg('');
          return;
        }
      } else {
        if (!recordData) {
          setStatus('error');
          setErrorMsg('Record data is not yet available.');
          return;
        }
      }

      setStatus('loading');
      setErrorMsg('');

      let google;
      try {
        google = await loadGoogleMapsApi();
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(err?.message || 'Failed to load Google Maps');
        return;
      }
      if (cancelled || !mapContainerRef.current) return;

      if (isRoute) {
        await renderRoute(google);
      } else {
        await renderSingle(google);
      }
    }

    render();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordData, resolved, widgetData, isRoute, zoom, isCollapsed, config, startLocation]);

  return (
    <Card className="mb-3 widget-card" style={{ width: '100%' }}>
      <Card.Header
        className="widget-card-header"
        style={{
          padding: '12px 16px',
          borderBottom: isCollapsed ? 'none' : '1px solid #dee2e6',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.colors.primary,
          color: 'rgb(255, 255, 255)'
        }}
        onClick={() => setIsCollapsed((v) => !v)}
      >
        <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600, color: 'rgb(255, 255, 255)' }}>
          {widget.widgetLabel || 'Map'}
          {statusInfo && (
            <span style={{ marginLeft: 8, fontWeight: 400, fontSize: '0.85em', opacity: 0.85 }}>
              · {statusInfo}
            </span>
          )}
        </h5>
        <i
          className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}
          style={{ color: 'rgb(255, 255, 255)', fontSize: '0.875rem' }}
        />
      </Card.Header>
      {!isCollapsed && (
        <Card.Body style={{ padding: 0, position: 'relative' }}>
          {isRoute && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
                padding: '8px 12px',
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb',
                fontSize: '0.85em'
              }}
            >
              <span style={{ fontWeight: 600, color: '#374151' }}>Start:</span>
              {(() => {
                // pick a color/text combo for the status pill
                let bg = '#f3f4f6';
                let fg = '#6b7280';
                let bd = '#e5e7eb';
                let txt = 'Not set (using record order)';
                if (startLocation) {
                  bg = '#dcfce7'; fg = '#166534'; bd = '#86efac';
                  txt = startLocation.label || `${startLocation.lat.toFixed(5)}, ${startLocation.lng.toFixed(5)}`;
                } else if (geoStatus === 'detecting') {
                  txt = 'Detecting current location…';
                } else if (geoStatus === 'denied') {
                  bg = '#fef2f2'; fg = '#b91c1c'; bd = '#fecaca';
                  txt = 'Location permission denied';
                } else if (geoStatus === 'insecure') {
                  bg = '#fef3c7'; fg = '#92400e'; bd = '#fcd34d';
                  txt = 'Geolocation needs HTTPS';
                } else if (geoStatus === 'unavailable') {
                  bg = '#fef3c7'; fg = '#92400e'; bd = '#fcd34d';
                  txt = 'Location unavailable';
                } else if (geoStatus === 'timeout') {
                  bg = '#fef3c7'; fg = '#92400e'; bd = '#fcd34d';
                  txt = 'Location request timed out';
                }
                return (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: bg,
                      color: fg,
                      border: `1px solid ${bd}`,
                      maxWidth: 360,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={startLocation?.label || txt}
                  >
                    <i className="fas fa-location-dot" style={{ fontSize: '0.85em' }} />
                    {txt}
                  </span>
                );
              })()}
              {(geoStatus === 'denied' || geoStatus === 'insecure' || geoStatus === 'unavailable' || geoStatus === 'timeout') && (
                <button
                  type="button"
                  onClick={() => setShowGeoHelp((v) => !v)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 8px',
                    border: '1px solid #fcd34d',
                    borderRadius: 6,
                    background: showGeoHelp ? '#fef3c7' : '#fffbeb',
                    color: '#92400e',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                  title="Show steps to enable location access"
                >
                  <i className="fas fa-circle-question" />
                  How to fix
                </button>
              )}
              <button
                type="button"
                onClick={detectCurrentLocation}
                disabled={geoStatus === 'detecting'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  background: '#fff',
                  color: '#374151',
                  cursor: geoStatus === 'detecting' ? 'wait' : 'pointer'
                }}
                title="Use my current location as the start"
              >
                <i className="fas fa-crosshairs" />
                {geoStatus === 'detecting' ? 'Detecting…' : 'My location'}
              </button>
              <button
                type="button"
                onClick={() => setShowStartAddrInput((v) => !v)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  background: showStartAddrInput ? '#eff6ff' : '#fff',
                  color: '#374151',
                  cursor: 'pointer'
                }}
                title="Set start by typing an address"
              >
                <i className="fas fa-magnifying-glass" />
                Address
              </button>
              {startLocation && (
                <button
                  type="button"
                  onClick={clearStartLocation}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    border: '1px solid #fecaca',
                    borderRadius: 6,
                    background: '#fff',
                    color: '#b91c1c',
                    cursor: 'pointer'
                  }}
                  title="Clear start location"
                >
                  <i className="fas fa-times" />
                  Clear
                </button>
              )}
              {showGeoHelp && (
                <div
                  style={{
                    flexBasis: '100%',
                    marginTop: 6,
                    padding: '10px 12px',
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: 8,
                    color: '#78350f',
                    fontSize: '0.85em',
                    lineHeight: 1.5
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    <i className="fas fa-triangle-exclamation me-2" />
                    {geoStatus === 'insecure'
                      ? 'Geolocation requires HTTPS'
                      : 'Enable location for this site'}
                  </div>
                  {geoStatus === 'insecure' ? (
                    <div>
                      Browsers only expose geolocation on <strong>secure pages</strong> (HTTPS or <code>localhost</code>).
                      Open this app via its HTTPS URL, then click <strong>My location</strong> again.
                      In the meantime you can set a start by typing an <strong>Address</strong>.
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: 6 }}>
                        Your browser is blocking location access. Follow the steps for your browser:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>
                          <strong>Chrome / Edge / Brave:</strong> click the <strong>lock icon</strong> at the
                          left of the address bar → <strong>Site settings</strong> → set
                          <strong> Location</strong> to <em>Allow</em>, then reload.
                        </li>
                        <li>
                          <strong>Safari (Mac) — two places must be allowed:</strong>
                          <ol style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                            <li>
                              <strong>macOS:</strong> <em>System Settings → Privacy &amp; Security →
                              Location Services</em> — turn Location Services <em>ON</em> and
                              tick <strong>Safari</strong> in the app list.
                            </li>
                            <li>
                              <strong>Safari:</strong> <em>Safari → Settings → Websites → Location</em> —
                              set this site to <em>Allow</em>.
                            </li>
                          </ol>
                          Then reload the tab and click <strong>My location</strong> again. Safari
                          remembers a dismissed prompt as “denied” — if it never asks you, remove
                          the site from Safari’s Location list and reload.
                        </li>
                        <li>
                          <strong>Firefox:</strong> click the <strong>lock icon</strong> →
                          <em> Connection secure → More information → Permissions</em>, remove the
                          “Access Your Location” block, then reload.
                        </li>
                        <li>
                          <strong>Mobile:</strong> also make sure the browser app itself has Location
                          permission in your phone’s OS settings.
                        </li>
                      </ul>
                      <div style={{ marginTop: 6 }}>
                        After enabling, click <strong>My location</strong> again. You can also set a
                        start by typing an <strong>Address</strong>.
                      </div>
                    </>
                  )}
                  <div style={{ marginTop: 8, textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => setShowGeoHelp(false)}
                      style={{
                        padding: '2px 8px',
                        border: '1px solid #fcd34d',
                        borderRadius: 6,
                        background: '#fff',
                        color: '#78350f',
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      Got it
                    </button>
                  </div>
                </div>
              )}
              {showStartAddrInput && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 240px', minWidth: 240 }}>
                  <input
                    type="text"
                    value={startAddrInput}
                    onChange={(e) => setStartAddrInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        geocodeStartAddress();
                      } else if (e.key === 'Escape') {
                        setShowStartAddrInput(false);
                      }
                    }}
                    placeholder="e.g. Shrinath Mall, Ajmer"
                    autoFocus
                    style={{
                      flex: 1,
                      padding: '4px 8px',
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      fontSize: '0.9em'
                    }}
                  />
                  <button
                    type="button"
                    onClick={geocodeStartAddress}
                    disabled={!startAddrInput.trim() || geocodingStart}
                    style={{
                      padding: '4px 10px',
                      border: '1px solid #1f6feb',
                      borderRadius: 6,
                      background: '#1f6feb',
                      color: '#fff',
                      cursor: !startAddrInput.trim() || geocodingStart ? 'not-allowed' : 'pointer',
                      opacity: !startAddrInput.trim() || geocodingStart ? 0.6 : 1
                    }}
                  >
                    {geocodingStart ? 'Searching…' : 'Set'}
                  </button>
                </div>
              )}
            </div>
          )}
          <div
            ref={mapContainerRef}
            style={{
              width: '100%',
              height: `${maxHeight}px`,
              minHeight: '200px',
              backgroundColor: '#f3f4f6'
            }}
          />
          {status !== 'ready' && (
            <div
              style={{
                position: 'absolute',
                top: isRoute ? 50 : 0, // leave room for the toolbar in route mode
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(243, 244, 246, 0.85)',
                color: status === 'error' ? '#b00020' : '#374151',
                fontSize: '0.9rem',
                padding: '16px',
                textAlign: 'center'
              }}
            >
              {status === 'loading' ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Loading map…
                </span>
              ) : (
                <span>
                  <i className="fas fa-exclamation-triangle me-2" />
                  {errorMsg}
                </span>
              )}
            </div>
          )}
          {isRoute && status === 'ready' && routeStops.length > 0 && (
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                padding: '8px 12px 12px',
                background: '#fff'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginBottom: 8
                }}
              >
                <div style={{ fontWeight: 600, color: '#111827' }}>
                  <i className="fas fa-route me-2" style={{ color: '#1f6feb' }} />
                  Visit sequence
                  <span style={{ marginLeft: 8, fontWeight: 400, color: '#6b7280', fontSize: '0.85em' }}>
                    {routeStops.length} stop{routeStops.length === 1 ? '' : 's'}
                    {startLocation ? ' · ordered from start' : ''}
                  </span>
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 12px',
                    background: '#eff6ff',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: '0.9em'
                  }}
                  title="Straight-line (great-circle) distance summed along the route"
                >
                  <i className="fas fa-road" />
                  Total: {totalDistanceKm.toFixed(2)} km
                </div>
              </div>
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  overflow: 'hidden'
                }}
              >
                {startLocation && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      background: '#f0fdf4',
                      borderBottom: '1px solid #e5e7eb'
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#22c55e',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.8em'
                      }}
                    >
                      S
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#166534', fontSize: '0.9em' }}>Start</div>
                      <div
                        style={{
                          color: '#374151',
                          fontSize: '0.85em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={startLocation.label || ''}
                      >
                        {startLocation.label ||
                          `${startLocation.lat.toFixed(5)}, ${startLocation.lng.toFixed(5)}`}
                      </div>
                    </div>
                  </div>
                )}
                {routeStops.map((s, i) => (
                  <div
                    key={`${s.recordId || s.index}-${i}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      borderTop: i === 0 && !startLocation ? 'none' : (i === 0 ? 'none' : '1px solid #f1f5f9'),
                      background: i % 2 === 0 ? '#ffffff' : '#f9fafb'
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: '#1f6feb',
                        color: '#fff',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.8em'
                      }}
                    >
                      {s.index}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: '#111827',
                          fontSize: '0.9em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={s.label}
                      >
                        {s.label}
                      </div>
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: '0.82em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={s.formattedAddress || ''}
                      >
                        {s.formattedAddress || (
                          <span style={{ fontStyle: 'italic' }}>
                            {`${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        flexShrink: 0,
                        textAlign: 'right',
                        fontSize: '0.85em',
                        color: '#374151',
                        minWidth: 110
                      }}
                    >
                      {Number.isFinite(s.distanceFromPrevKm) ? (
                        <>
                          <div style={{ fontWeight: 600 }}>
                            {s.distanceFromPrevKm.toFixed(2)} km
                          </div>
                          <div style={{ color: '#9ca3af', fontSize: '0.85em' }}>
                            {i === 0 && startLocation ? 'from start' : `from #${i}`}
                          </div>
                        </>
                      ) : (
                        <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 6, color: '#9ca3af', fontSize: '0.75em' }}>
                Distances are straight-line (great-circle) estimates. Actual driving distance may differ.
              </div>
            </div>
          )}
        </Card.Body>
      )}
    </Card>
  );
};

/**
 * LayoutWidget Component
 * Displays a single widget in card format
 * Supports different widget types (relatedlist, activity, chatter, etc.)
 */
const LayoutWidget = ({ widget, onRefresh, currentRecordId, sourceObjectName, onShowAll }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLogActivityModal, setShowLogActivityModal] = useState(false);
  const [logActivityType, setLogActivityType] = useState(null); // 'call', 'meeting', 'note', 'email', 'followup'
  const [showActivityChooser, setShowActivityChooser] = useState(false);
  const [showEmailSender, setShowEmailSender] = useState(false);
  const [recordDataForEmail, setRecordDataForEmail] = useState(null);
  const [loadingRecordData, setLoadingRecordData] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isOtherCollapsed, setIsOtherCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFileRecord, setPreviewFileRecord] = useState(null);

  // Debug: Log when activeTab changes
  useEffect(() => {
    console.log('activeTab state changed to:', activeTab);
  }, [activeTab]);

  // Close activity chooser when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActivityChooser && !event.target.closest('[data-activity-chooser]')) {
        setShowActivityChooser(false);
      }
    };

    if (showActivityChooser) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActivityChooser]);

  // Fetch record data when EmailSender modal opens
  useEffect(() => {
    if (showEmailSender && currentRecordId && sourceObjectName && !recordDataForEmail) {
      setLoadingRecordData(true);
      const dataApi = new DataApi(`object/view/${sourceObjectName}`);
      dataApi.fetchById(currentRecordId)
        .then(response => {
          if (response?.data?.success && response?.data?.data) {
            setRecordDataForEmail(response.data.data);
          } else {
            console.error('Failed to fetch record data for email');
          }
        })
        .catch(error => {
          console.error('Error fetching record data for email:', error);
        })
        .finally(() => {
          setLoadingRecordData(false);
        });
    }

    // Reset recordData when modal closes
    if (!showEmailSender && recordDataForEmail) {
      setRecordDataForEmail(null);
      setLoadingRecordData(false);
    }
  }, [showEmailSender, currentRecordId, sourceObjectName, recordDataForEmail]);

  // Handle tab selection
  const handleTabSelect = (selectedKey) => {
    console.log('Tab selected:', selectedKey, 'Current activeTab:', activeTab);
    if (selectedKey !== activeTab) {
      setActiveTab(selectedKey);
      console.log('ActiveTab updated to:', selectedKey);
    } else {
      console.log('Tab already active, no state change needed');
    }
  };

  // Extract values for useMemo (must be at top level)
  // These will be used conditionally, but hooks must be at top level
  const widgetRecords = widget?.data?.records || [];
  const widgetPicklistField = widget?.config?.picklistField || widget?.data?.picklistField;
  const widgetPicklistValues = widget?.data?.picklistValues || [];
  const widgetMaxRecords = widget?.widgetType === 'relatedlist' && widget?.data
    ? Math.min(widget.config?.maxRecords || 10, 20)
    : 20;

  // Group records by picklist value if picklist field is configured (must be at top level)
  const groupedRecords = useMemo(() => {
    // Only compute if we have a relatedlist widget with data
    if (widget?.widgetType !== 'relatedlist' || !widget?.data || widgetRecords.length === 0) {
      return { 'all': [] };
    }

    if (!widgetPicklistField || !widgetPicklistValues || widgetPicklistValues.length === 0) {
      return { 'all': widgetRecords.slice(0, widgetMaxRecords) };
    }

    // Initialize groups with all picklist values
    const groups = {};
    widgetPicklistValues.forEach(pv => {
      groups[pv.key] = [];
    });
    groups['all'] = []; // Add "All" tab

    // Group records by picklist value
    widgetRecords.forEach(record => {
      const picklistValue = record[widgetPicklistField];
      if (picklistValue && groups[picklistValue]) {
        groups[picklistValue].push(record);
      }
      groups['all'].push(record);
    });

    // Limit records per group to maxRecords
    Object.keys(groups).forEach(key => {
      groups[key] = groups[key].slice(0, widgetMaxRecords);
    });

    return groups;
  }, [widget?.widgetType, widget?.data, widgetRecords, widgetPicklistField, widgetPicklistValues, widgetMaxRecords]);

  if (!widget || !widget.widgetType) return null;

  // Check if this is an Activity Timeline widget
  // Match RelatedTabsModal logic: target_object_name === 'activity' && target_field_name === 'related_record_id' && source_field_name === 'id'
  // It can be either widgetType === 'activity' OR a relatedlist widget with activity target
  const isActivityTimeline = widget.widgetType === 'activity' ||
    (widget.widgetType === 'relatedlist' &&
      widget.data?.targetObjectName === 'activity' &&
      widget.data?.targetFieldName === 'related_record_id' &&
      (widget.data?.sourceFieldName === 'id' || !widget.data?.sourceFieldName)); // sourceFieldName might not always be present

  // For activity widgets, display activity timeline
  if (isActivityTimeline) {
    console.log('LayoutWidget - Activity widget data:', widget?.data);
    console.log('LayoutWidget - Activity widget records:', widget?.data?.records);
    console.log('LayoutWidget - Activity widget records length:', widget?.data?.records?.length);
    if (widget?.data?.records && widget.data.records.length > 0) {
      console.log('LayoutWidget - First record:', widget.data.records[0]);
      console.log('LayoutWidget - First record fields:', widget.data.records[0]?.fields);
    }

    // Check if widget data is still loading (widget.data might be undefined initially)
    const isWidgetLoading = !widget?.data;
    const widgetRecords = widget?.data?.records || [];

    return (
      <>
        <Card className="mb-3 widget-card" style={{ width: '100%' }}>
          <Card.Header
            className="widget-card-header"
            style={{
              padding: '12px 16px',
              borderBottom: isCollapsed ? 'none' : '1px solid #dee2e6',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: theme.colors.primary,
              color: 'rgb(255, 255, 255)'
            }}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600, color: 'rgb(255, 255, 255)' }}>
              {widget.widgetLabel || 'Activity Timeline'}
            </h5>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: '0 0 auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Log Activity Chooser */}
              <div style={{ position: 'relative' }} data-activity-chooser>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentRecordId && sourceObjectName) {
                      setShowActivityChooser(!showActivityChooser);
                    }
                  }}
                  style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'rgb(255, 255, 255)',
                    margin: '0px',
                    flex: '0 0 auto',
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title="Log Activity"
                >
                  <i className="fas fa-plus" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                  Log Activity
                </button>

                {/* Activity Type Chooser Dropdown */}
                {showActivityChooser && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      minWidth: '180px',
                      padding: '4px 0'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{
                        padding: '8px 16px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#666',
                        borderBottom: '1px solid #dee2e6',
                        marginBottom: '4px'
                      }}
                    >
                      Log Activity
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('call');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-phone" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Log Call</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('meeting');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-calendar" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Log Meeting</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('note');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-sticky-note" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Add Note</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setShowActivityChooser(false);
                        // Show modal immediately
                        if (currentRecordId && sourceObjectName) {
                          setShowEmailSender(true);
                        }
                      }}
                    >
                      <i className="fas fa-envelope" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Log Email</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('followup');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-clock" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Add Follow-up</span>
                    </div>
                  </div>
                )}
              </div>
              <i
                className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}
                style={{
                  color: 'rgb(255, 255, 255)',
                  fontSize: '0.875rem',
                  transition: 'transform 0.2s ease'
                }}
              />
            </div>
          </Card.Header>
          {!isCollapsed && (
            <Card.Body style={{ padding: '0', maxHeight: widget.config?.maxHeight || 600, overflowY: 'auto' }}>
              {isWidgetLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                  Loading activities...
                </div>
              ) : (
                <TaskActivityTimeline
                  recordId={currentRecordId}
                  objectName={sourceObjectName}
                  onRefresh={onRefresh}
                  hideLogActivityButton={true}
                  records={widgetRecords}
                />
              )}
            </Card.Body>
          )}
        </Card>

        {/* Email Sender Modal */}
        {showEmailSender && sourceObjectName && (
          <EmailSender
            show={showEmailSender}
            onHide={() => {
              setShowEmailSender(false);
              setRecordDataForEmail(null);
              setLoadingRecordData(false);
            }}
            recordData={recordDataForEmail} // Can be null while loading
            objectName={sourceObjectName}
            currentUser={(() => {
              try {
                const token = localStorage.getItem('token');
                if (token) {
                  const decoded = jwt_decode(token);
                  return {
                    id: decoded.id,
                    firstname: decoded.firstname,
                    lastname: decoded.lastname,
                    username: decoded.username,
                    email: decoded.email,
                  };
                }
              } catch (error) {
                console.error('Failed to decode current user token:', error);
              }
              return null;
            })()}
            onEmailSent={(emailData) => {
              console.log('Email sent successfully:', emailData);
              setShowEmailSender(false);
              setRecordDataForEmail(null);
              // Refresh the widget data
              if (onRefresh) {
                onRefresh();
              }
            }}
          />
        )}

        {/* Log Activity Modal */}
        {showLogActivityModal && currentRecordId && sourceObjectName && logActivityType && (
          <EditLayoutModal
            show={showLogActivityModal}
            onHide={() => {
              setShowLogActivityModal(false);
              setLogActivityType(null);
            }}
            objectName={
              logActivityType === 'note' ? 'note' :
                logActivityType === 'email' ? 'email_log' :
                  'task' // call, meeting, followup all use 'task'
            }
            id={null}
            sourcePage="detail"
            onSave={(savedData) => {
              setShowLogActivityModal(false);
              setLogActivityType(null);
              // Refresh the widget data
              if (onRefresh) {
                onRefresh();
              }
            }}
            onCancel={() => {
              setShowLogActivityModal(false);
              setLogActivityType(null);
            }}
            onRefresh={onRefresh}
            queryParams={
              logActivityType === 'note' ? {
                targetField: 'parent_id', // Notes use parent_id instead of related_record_id
                targetId: currentRecordId,
                parentObjectName: sourceObjectName
              } :
                logActivityType === 'email' ? {
                  targetField: 'related_record_id',
                  targetId: currentRecordId,
                  parentObjectName: sourceObjectName
                } :
                  {
                    targetField: 'related_record_id',
                    targetId: currentRecordId,
                    parentObjectName: sourceObjectName,
                    taskType: logActivityType === 'call' ? 'Call' :
                      logActivityType === 'meeting' ? 'Meeting' :
                        logActivityType === 'followup' ? 'Follow-up' : 'Call'
                  }
            }
          />
        )}
      </>
    );
  }

  // For map widgets, render Google Map using fields from current record
  if (widget.widgetType === 'map') {
    return <MapWidgetView widget={widget} />;
  }

  // For score widgets, display ScoreDisplay component
  if (widget.widgetType === 'score') {
    const scoreMaxHeightValue = widget.config?.max_height ?? widget.config?.maxHeight;
    const scoreMaxHeight = scoreMaxHeightValue !== undefined && scoreMaxHeightValue !== null
      ? (typeof scoreMaxHeightValue === 'string' ? parseInt(scoreMaxHeightValue, 10) : scoreMaxHeightValue)
      : 400;
    return (
      <Card className="mb-3 widget-card" style={{ width: '100%', maxHeight: `${scoreMaxHeight}px`, display: 'flex', flexDirection: 'column' }}>
        <Card.Body style={{ padding: 0, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <ScoreDisplay objectName={sourceObjectName} recordId={currentRecordId} />
        </Card.Body>
      </Card>
    );
  }

  // For search widgets: table (related-list style actions) or card layout
  if (widget.widgetType === 'search') {
    const useTable = widget.config?.display_as_table === true;
    const sharedProps = {
      widget,
      currentRecordId,
      sourceObjectName,
      recordData: widget.recordData
    };
    return useTable ? (
      <SearchViewRecordsTable {...sharedProps} maxHeight={widget.config?.max_height || 480} />
    ) : (
      <SearchWidget {...sharedProps} onRefresh={onRefresh} />
    );
  }

  // For relatedlist widgets, display records in card style
  // Skip Activity Timeline widgets as they're handled above
  if (widget.widgetType === 'relatedlist' && widget.data && !isActivityTimeline) {
    const records = widget.data.records || [];
    const targetObjectName = widget.data.targetObjectName || widget.config?.targetObjectName;
    // widget.data.fields is now an array of {fieldName, fieldLabel, dataType}
    // Fallback to widget.config.fields (array of field names) for backward compatibility
    const fieldMetadata = widget.data.fields || widget.config?.fields?.map(fieldName => ({ fieldName, fieldLabel: fieldName })) || [];
    console.log('LayoutWidget - fieldMetadata:', JSON.stringify(fieldMetadata, null, 2));
    console.log('LayoutWidget - widget.data:', widget.data);
    console.log('LayoutWidget - widget.config:', widget.config);
    const maxRecords = Math.min(widget.config?.maxRecords || 10, 20); // Max 20 records
    // Parse maxHeight - handle both number and string, ensure it's a valid number
    const maxHeightValue = widget.config?.maxHeight;
    const maxHeight = maxHeightValue !== undefined && maxHeightValue !== null
      ? (typeof maxHeightValue === 'string' ? parseInt(maxHeightValue, 10) : maxHeightValue)
      : 400;

    // Picklist field configuration
    const picklistField = widget.config?.picklistField || widget.data?.picklistField;
    const picklistValues = widget.data?.picklistValues || [];

    // Helper to normalize picklist values that may come as objects
    const normalizePicklistValue = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value !== 'object' || Array.isArray(value)) return value;
      if (value.value !== undefined && value.value !== null) return value.value;
      if (value.displayValue !== undefined && value.displayValue !== null) return value.displayValue;
      return null;
    };

    // Recompute groupedRecords with actual records from this block
    // (useMemo at top level might have computed with empty array, so recompute here)
    let actualGroupedRecords = {};
    if (records.length > 0 && picklistField && picklistValues && picklistValues.length > 0) {
      // Initialize groups with all picklist values
      picklistValues.forEach(pv => {
        actualGroupedRecords[pv.key] = [];
      });
      actualGroupedRecords['all'] = []; // Add "All" tab

      // Group records by picklist value
      records.forEach(record => {
        const picklistValue = normalizePicklistValue(record[picklistField]);
        const picklistKey = picklistValue !== null && picklistValue !== undefined ? String(picklistValue) : null;
        console.log('Grouping record:', { picklistField, picklistValue, record });

        // Add to specific picklist group if it matches
        if (picklistKey && actualGroupedRecords[picklistKey]) {
          actualGroupedRecords[picklistKey].push(record);
        }
        // Always add to "all" group
        actualGroupedRecords['all'].push(record);
      });

      // Limit records per group to maxRecords
      Object.keys(actualGroupedRecords).forEach(key => {
        actualGroupedRecords[key] = actualGroupedRecords[key].slice(0, maxRecords);
      });

      console.log('Final groupedRecords:', actualGroupedRecords);
    } else if (records.length > 0) {
      // No picklist grouping, just use all records
      actualGroupedRecords = { 'all': records.slice(0, maxRecords) };
      console.log('No picklist - using all records:', actualGroupedRecords);
    } else {
      actualGroupedRecords = { 'all': [] };
      console.log('No records available');
    }

    // Debug logging
    console.log('LayoutWidget - Debug:', {
      recordsCount: records.length,
      picklistField,
      picklistValuesCount: picklistValues.length,
      actualGroupedRecords,
      activeTab,
      maxRecords,
      displayRecordsCount: picklistField && picklistValues.length > 0
        ? (actualGroupedRecords[activeTab] || []).length
        : records.slice(0, maxRecords).length
    });

    const displayRecords = picklistField && picklistValues.length > 0
      ? (actualGroupedRecords[activeTab] || [])
      : records.slice(0, maxRecords);

    // Helper to get record ID value - must be defined before use in map
    const getRecordIdValue = (record) => {
      if (!record) return null;
      if (record.id && typeof record.id === 'string') {
        return record.id;
      }
      if (record.id && typeof record.id === 'object' && record.id.value) {
        return record.id.value;
      }
      return record.id || null;
    };

    // Resolve currency with priority: token currency (user or company) > default
    const resolveCurrency = (fallbackCurrency = 'INR') => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = jwt_decode(token);
          if (decoded?.currency) {
            return decoded.currency;
          }
        }
      } catch (error) {
        console.error('Error decoding token for currency:', error);
      }
      return fallbackCurrency;
    };

    // Helper function to safely extract primitive value from potentially nested objects
    const extractPrimitiveValue = (value) => {
      if (value === null || value === undefined) {
        return null;
      }

      // If it's already a primitive (not an object or array), return it
      if (typeof value !== 'object' || Array.isArray(value)) {
        return value;
      }

      // If it's an object, try to extract a primitive value
      // Priority: value > displayValue > convert to string
      if (value.value !== undefined) {
        // Recursively extract if value is also an object
        const extracted = extractPrimitiveValue(value.value);
        if (extracted !== null) return extracted;
      }

      if (value.displayValue !== undefined) {
        const extracted = extractPrimitiveValue(value.displayValue);
        if (extracted !== null) return extracted;
      }

      // If we can't extract a primitive, convert to string as last resort
      try {
        const stringValue = String(value);
        return stringValue === '[object Object]' ? null : stringValue;
      } catch {
        return null;
      }
    };

    // Helper function to get currency from record
    const getRecordCurrency = (record) => {
      if (!record) return null;

      const currencyField = record.currency || record.currency_code || record.Currency;
      if (currencyField) {
        return extractPrimitiveValue(currencyField);
      }

      if (Array.isArray(record.fields)) {
        const currencyMeta = record.fields.find(field =>
          String(field?.fieldName || '').toLowerCase() === 'currency'
        );
        if (currencyMeta) {
          const currencyValue = currencyMeta.value ?? currencyMeta.displayValue;
          const extracted = extractPrimitiveValue(currencyValue);
          if (extracted) {
            return extracted;
          }
        }
      }

      return null;
    };

    // Check if this is an attachment widget
    const isAttachmentWidget = targetObjectName && (
      targetObjectName.toLowerCase() === 'attachment' ||
      widget.widgetLabel?.toLowerCase().includes('attachment')
    );

    // Action handlers
    const handleViewAttachment = (record) => {
      console.log('handleViewAttachment called with record:', record);
      setPreviewFileRecord(record);
      setShowPreviewModal(true);
      setActiveDropdown(null);
    };

    const handleViewRecord = (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId || !targetObjectName) return;

      // For attachments, show preview instead of navigating to detail page
      if (isAttachmentWidget) {
        handleViewAttachment(record);
      } else {
        window.open(`/view/${targetObjectName}/${recordId}`, '_blank');
      }
    };

    const handleEditRecord = (record) => {
      // Don't allow editing attachments
      if (isAttachmentWidget) return;

      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setEditingRecord({ ...record, id: { value: recordId } });
      setShowEditModal(true);
      setActiveDropdown(null);
    };

    const handleCloneRecord = (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setEditingRecord({ ...record, id: { value: recordId } });
      setShowCloneModal(true);
      setActiveDropdown(null);
    };

    const handleDeleteRecord = (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setRecordToDelete(record);
      setShowDeleteModal(true);
      setActiveDropdown(null);
    };

    const confirmDeleteRecord = async () => {
      if (!recordToDelete || !targetObjectName) return;

      const recordId = getRecordIdValue(recordToDelete);
      if (!recordId) return;

      try {
        const dataApi = new DataApi('object');
        const response = await dataApi.delete(`/${targetObjectName}/${recordId}`);

        if (response.data.success) {
          // Refresh the widget data
          if (onRefresh) {
            onRefresh();
          }
          setShowDeleteModal(false);
          setRecordToDelete(null);
        } else {
          alert(`Failed to delete record: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error deleting record:', error);
        alert(`Error deleting record: ${error.message}`);
      }
    };

    const handleModalSaveSuccess = () => {
      setShowEditModal(false);
      setShowCloneModal(false);
      setEditingRecord(null);
      // Refresh the widget data
      if (onRefresh) {
        onRefresh();
      }
    };

    const handleModalCancel = () => {
      setShowEditModal(false);
      setShowCloneModal(false);
      setEditingRecord(null);
    };

    // Helper function to render record cards
    const renderRecordCards = (recordsToRender, totalRecordsCount = null) => {
      console.log('renderRecordCards called with:', {
        count: recordsToRender?.length || 0,
        totalRecords: totalRecordsCount,
        records: recordsToRender,
        isArray: Array.isArray(recordsToRender),
        firstRecord: recordsToRender?.[0]
      });

      if (!recordsToRender || !Array.isArray(recordsToRender) || recordsToRender.length === 0) {
        console.log('renderRecordCards: No records to render');
        return (
          <p className="text-muted mb-0" style={{ fontSize: theme.typography.fontSize.lg }}>
            No records found
          </p>
        );
      }

      console.log('renderRecordCards: Rendering', recordsToRender.length, 'records');
      console.log('renderRecordCards: fieldMetadata available?', fieldMetadata?.length > 0, fieldMetadata);
      return (
        <div className="widget-records" style={{ width: '100%' }}>
          {recordsToRender.map((record, idx) => {
            const recordId = getRecordIdValue(record);
            const recordIdValue = recordId || `record-${idx}`;
            console.log(`renderRecordCards: Rendering record ${idx}:`, record);

            return (
              <Card key={idx} className="mb-2 widget-record-card" style={{ border: '1px solid #e0e0e0', width: '100%', position: 'relative' }}>
                <Card.Body className="p-3">
                  {/* Actions Dropdown - Top Right Corner */}
                  <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
                    <Dropdown
                      onToggle={(isOpen) => {
                        setActiveDropdown(isOpen ? recordIdValue : null);
                      }}
                      show={activeDropdown === recordIdValue}
                      className="widget-actions-dropdown"
                    >
                      <Dropdown.Toggle
                        variant="light"
                        size="sm"
                        id={`widget-dropdown-${recordIdValue}`}
                        className="widget-card-dropdown-toggle"
                        style={{
                          padding: '2px 8px',
                          fontSize: '14px',
                          border: '1px solid #dee2e6',
                          backgroundColor: 'white',
                          color: '#6c757d',
                          lineHeight: 1,
                          opacity: activeDropdown === recordIdValue ? 1 : 0,
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          if (activeDropdown !== recordIdValue) {
                            e.target.style.opacity = '0';
                          }
                        }}
                      >
                        <span style={{ color: '#6c757d', fontWeight: 'bold' }}>&#9660;</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end" className="widget-dropdown-menu">
                        {isAttachmentWidget ? (
                          <>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewAttachment(record);
                              }}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                const recordId = getRecordIdValue(record);
                                if (recordId) {
                                  const dataApi = new DataApi('attachments');
                                  dataApi.get(`/download/${recordId}`, { responseType: 'blob' })
                                    .then(response => {
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      const fileName = record.file_name?.value || record.file_name || 'download';
                                      link.setAttribute('download', fileName);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    })
                                    .catch(error => {
                                      console.error('Error downloading file:', error);
                                      alert('Failed to download file');
                                    });
                                }
                              }}
                            >
                              <i className="fas fa-download me-2"></i>
                              Download
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRecord(record);
                              }}
                              className="text-danger"
                            >
                              <i className="fas fa-trash me-2"></i>
                              Delete
                            </Dropdown.Item>
                          </>
                        ) : (
                          <>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRecord(record);
                              }}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditRecord(record);
                              }}
                            >
                              <i className="fas fa-edit me-2"></i>
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCloneRecord(record);
                              }}
                            >
                              <i className="fas fa-clone me-2"></i>
                              Clone
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRecord(record);
                              }}
                              className="text-danger"
                            >
                              <i className="fas fa-trash me-2"></i>
                              Delete
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <Row className="g-2" style={{ paddingRight: '30px' }}>
                    {fieldMetadata && fieldMetadata.length > 0 ? (() => {
                      // Filter out 'id' field from display
                      const filteredFields = fieldMetadata.filter(fieldMeta => {
                        const fieldName = typeof fieldMeta === 'object' ? fieldMeta.fieldName : fieldMeta;
                        return fieldName?.toLowerCase() !== 'id';
                      });

                      // Only apply special title/description layout for notes
                      const isNotesWidget = targetObjectName && (
                        targetObjectName.toLowerCase() === 'note' ||
                        targetObjectName.toLowerCase() === 'notes'
                      );

                      // Find title/name and description fields (only for notes)
                      const titleFieldNames = ['name', 'title', 'subject'];
                      const descriptionFieldName = 'description';

                      let titleField = null;
                      let descriptionField = null;
                      const otherFields = [];

                      if (isNotesWidget) {
                        // For notes, separate title and description for special layout
                        filteredFields.forEach(fieldMeta => {
                          const fieldName = typeof fieldMeta === 'object' ? fieldMeta.fieldName : fieldMeta;
                          const fieldNameLower = fieldName?.toLowerCase();

                          if (titleFieldNames.includes(fieldNameLower) && !titleField) {
                            titleField = fieldMeta;
                          } else if (fieldNameLower === descriptionFieldName && !descriptionField) {
                            descriptionField = fieldMeta;
                          } else {
                            otherFields.push(fieldMeta);
                          }
                        });
                      } else {
                        // For other widgets (like tasks), treat all fields equally in 2-column layout
                        otherFields.push(...filteredFields);
                      }

                      // Format file size helper function
                      const formatFileSize = (bytes) => {
                        if (!bytes || bytes === 0) return '0 Bytes';
                        // Handle both number and string inputs
                        const sizeInBytes = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
                        if (isNaN(sizeInBytes) || sizeInBytes === 0) return '0 Bytes';
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
                        return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                      };

                      const renderField = (fieldMeta, colSize = 6) => {
                        // Handle both object format {fieldName, fieldLabel} and string format
                        const fieldName = typeof fieldMeta === 'object' ? fieldMeta.fieldName : fieldMeta;
                        const fieldLabel = typeof fieldMeta === 'object' ? fieldMeta.fieldLabel : fieldMeta;
                        const dataType = typeof fieldMeta === 'object' ? fieldMeta.dataType : 'text';
                        const referenceObject = typeof fieldMeta === 'object' ? fieldMeta.referenceObject : null;

                        // Check if this is a file size field
                        const isFileSizeField = fieldName && (
                          fieldName.toLowerCase() === 'file_size' ||
                          fieldName.toLowerCase() === 'filesize' ||
                          fieldName.toLowerCase() === 'size' ||
                          fieldName.toLowerCase().endsWith('_size')
                        );

                        // Get field value - handle both plain value and enriched lookup object
                        const fieldData = record[fieldName];
                        let fieldValue = '—';
                        let displayValue = null;
                        let isLookup = false;
                        let isRichText = false;
                        let isUrl = false;
                        let lookupReferenceObject = null;
                        let lookupId = null;
                        const normalizedType = typeof dataType === 'string' ? dataType.toLowerCase() : '';
                        const recordCurrency = getRecordCurrency(record);

                        const getDisplayConfig = () => {
                          return fieldData?.displayConfig ??
                            fieldData?.display_config ??
                            fieldMeta?.displayConfig ??
                            fieldMeta?.display_config ??
                            null;
                        };

                        const getCurrencyCode = () => {
                          return recordCurrency ||
                            fieldData?.currencyCode ||
                            fieldMeta?.currencyCode ||
                            resolveCurrency('INR');
                        };

                        // Check if field is richtext type
                        const fieldType = typeof fieldData === 'object' && fieldData !== null ? fieldData.type : null;
                        isRichText = dataType === 'richtext' || fieldType === 'richtext';
                        isUrl = dataType === 'url' || fieldType === 'url';

                        if (fieldData !== null && fieldData !== undefined) {
                          // Check if it's an enriched lookup field object
                          if (typeof fieldData === 'object' && fieldData.isReference) {
                            isLookup = true;
                            lookupId = extractPrimitiveValue(fieldData.value);
                            const extractedDisplayValue = extractPrimitiveValue(fieldData.displayValue);
                            const extractedValue = extractPrimitiveValue(fieldData.value);
                            displayValue = extractedDisplayValue || extractedValue;
                            lookupReferenceObject = fieldData.referenceObject || referenceObject;
                            fieldValue = displayValue || '—';
                          } else if (typeof fieldData === 'object' && (fieldData.isPicklist || fieldData.isMultiPicklist)) {
                            // Handle picklist fields - use displayValue if available, otherwise value
                            const extractedDisplayValue = extractPrimitiveValue(fieldData.displayValue);
                            const extractedValue = extractPrimitiveValue(fieldData.value);
                            fieldValue = extractedDisplayValue || extractedValue || '—';
                          } else if (dataType === 'lookup' && referenceObject && fieldData) {
                            // Fallback: if it's a lookup field but not enriched, treat it as lookup
                            isLookup = true;
                            lookupId = extractPrimitiveValue(fieldData);
                            displayValue = extractPrimitiveValue(fieldData);
                            lookupReferenceObject = referenceObject;
                            fieldValue = displayValue || '—';
                          } else if ((dataType === 'picklist' || dataType === 'multipicklist') && typeof fieldData === 'object' && fieldData !== null) {
                            // Handle picklist fields that might be objects from fetchRecords
                            const extractedDisplayValue = extractPrimitiveValue(fieldData.displayValue);
                            const extractedValue = extractPrimitiveValue(fieldData.value);
                            fieldValue = extractedDisplayValue || extractedValue || '—';
                          } else if (normalizedType === 'currency') {
                            const rawValue = extractPrimitiveValue(fieldData);
                            const numValue = Number(rawValue);
                            const decimalPlaces = (fieldData?.scale ?? fieldMeta?.scale) !== undefined &&
                              (fieldData?.scale ?? fieldMeta?.scale) !== null &&
                              (fieldData?.scale ?? fieldMeta?.scale) !== ''
                              ? parseInt(fieldData?.scale ?? fieldMeta?.scale, 10)
                              : 2;
                            if (!Number.isNaN(numValue)) {
                              fieldValue = formatCurrency(numValue, getCurrencyCode(), {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            } else {
                              fieldValue = formatCurrency(0, getCurrencyCode(), {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            }
                          } else if (normalizedType === 'rollupsummary') {
                            const rawValue = extractPrimitiveValue(fieldData);
                            const displayConfig = getDisplayConfig();
                            let parsedConfig = {};
                            if (displayConfig) {
                              if (typeof displayConfig === 'string') {
                                try {
                                  parsedConfig = JSON.parse(displayConfig);
                                } catch {
                                  parsedConfig = {};
                                }
                              } else {
                                parsedConfig = displayConfig || {};
                              }
                            }
                            const rollupConfig = parsedConfig.rollupSummary || {};
                            const outputType = (rollupConfig.outputType || 'number').toLowerCase();
                            const numValue = Number(rawValue);
                            if (outputType === 'currency') {
                              const decimalPlaces = (fieldData?.scale ?? fieldMeta?.scale) !== undefined &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== null &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== ''
                                ? parseInt(fieldData?.scale ?? fieldMeta?.scale, 10)
                                : 2;
                              const safeValue = Number.isNaN(numValue) ? 0 : numValue;
                              fieldValue = formatCurrency(safeValue, getCurrencyCode(), {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            } else if (outputType === 'date') {
                              fieldValue = rawValue ? formatDate(rawValue) : '';
                            } else if (outputType === 'datetime') {
                              fieldValue = rawValue ? formatDateTime(rawValue) : '';
                            } else if (outputType === 'time') {
                              fieldValue = rawValue ? formatTime(rawValue) : '';
                            } else if (!Number.isNaN(numValue)) {
                              const decimalPlaces = (fieldData?.scale ?? fieldMeta?.scale) !== undefined &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== null &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== ''
                                ? parseInt(fieldData?.scale ?? fieldMeta?.scale, 10)
                                : undefined;
                              fieldValue = formatNumber(numValue, {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            } else {
                              fieldValue = '';
                            }
                          } else if (normalizedType === 'calculated') {
                            const rawValue = extractPrimitiveValue(fieldData);
                            const displayConfig = getDisplayConfig();
                            const normalized = normalizeCalculatedDisplayConfig(displayConfig);
                            if (normalized.returnType === 'currency') {
                              const decimalPlaces = normalized.decimalPlaces ?? 2;
                              const numValue = Number(rawValue);
                              const safeValue = Number.isNaN(numValue) ? 0 : numValue;
                              const currencyFromConfig = typeof normalized.currencySymbol === 'string'
                                ? normalized.currencySymbol.trim()
                                : '';

                              if (currencyFromConfig && !/^[A-Z]{3}$/.test(currencyFromConfig)) {
                                fieldValue = formatCalculatedValue(safeValue, displayConfig);
                              } else {
                                fieldValue = formatCurrency(safeValue, currencyFromConfig || getCurrencyCode(), {
                                  minimumFractionDigits: decimalPlaces,
                                  maximumFractionDigits: decimalPlaces
                                });
                              }
                            } else {
                              fieldValue = formatCalculatedValue(rawValue, displayConfig);
                            }
                          } else if (isRichText) {
                            // Rich text field - get the HTML value
                            const extractedValue = extractPrimitiveValue(fieldData);
                            fieldValue = extractedValue !== null ? extractedValue : fieldData;
                          } else if (isUrl) {
                            // URL field - extract the URL value
                            const extractedValue = extractPrimitiveValue(fieldData);
                            fieldValue = extractedValue !== null ? extractedValue : '—';
                          } else if (isFileSizeField) {
                            // File size field - format as KB/MB
                            const extractedValue = extractPrimitiveValue(fieldData);
                            if (extractedValue !== null && extractedValue !== undefined && extractedValue !== '—') {
                              fieldValue = formatFileSize(extractedValue);
                            } else {
                              fieldValue = '—';
                            }
                          } else {
                            // Regular field value - extract primitive value
                            const rawValue = extractPrimitiveValue(fieldData);
                            const isNumericType = ['currency', 'number', 'integer', 'decimal', 'rollupsummary'].includes(normalizedType);

                            // Create a field object for formatFieldValueUtil (matches DetailLayout format)
                            const fieldForFormatting = {
                              fieldName: fieldName,
                              label: fieldLabel,
                              type: dataType,
                              value: rawValue,
                              displayValue: typeof fieldData === 'object' && fieldData !== null ? fieldData.displayValue : null,
                              scale: typeof fieldData === 'object' && fieldData !== null ? fieldData.scale : null,
                              precision: typeof fieldData === 'object' && fieldData !== null ? fieldData.precision : null,
                              displayConfig: typeof fieldData === 'object' && fieldData !== null ? fieldData.displayConfig : null
                            };

                            // Use formatFieldValueUtil for consistent formatting (handles dates, numbers, currency, etc.)
                            // This matches how DetailLayout formats field values
                            if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                              const formatted = formatFieldValueUtil(fieldForFormatting);
                              fieldValue = formatted !== null && formatted !== undefined ? formatted : '—';
                            } else {
                              if (isNumericType) {
                                const formattedZero = formatFieldValueUtil({ ...fieldForFormatting, value: 0 });
                                fieldValue = formattedZero !== null && formattedZero !== undefined ? formattedZero : '0';
                              } else {
                                fieldValue = '';
                              }
                            }
                          }
                        }

                        return (
                          <Col key={fieldName} xs={colSize} className="widget-field mb-2">
                            <div
                              className="d-block mb-1"
                              style={{
                                fontFamily: theme.typography.fontFamily.primary,
                                fontWeight: theme.typography.fontWeight.medium,
                                color: theme.colors.textSecondary,
                                lineHeight: theme.typography.lineHeight.normal,
                                fontSize: theme.typography.fontSize.lg
                              }}
                            >
                              {fieldLabel}
                            </div>
                            <div
                              style={{
                                fontFamily: theme.typography.fontFamily.primary,
                                fontWeight: theme.typography.fontWeight.normal,
                                color: theme.colors.textPrimary,
                                lineHeight: theme.typography.lineHeight.normal,
                                fontSize: theme.typography.fontSize.lg,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%'
                              }}
                              className={isRichText ? 'richtext-value rich-text-content' : ''}
                              title={fieldValue && fieldValue !== '—' ? String(fieldValue) : ''}
                            >
                              {isRichText ? (
                                <div dangerouslySetInnerHTML={{ __html: fieldValue || '—' }} />
                              ) : isUrl && fieldValue && fieldValue !== '—' ? (
                                <a
                                  href={fieldValue}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link-brand"
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    textDecoration: 'none',
                                    transition: theme.transitions.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.textDecoration = 'underline';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none';
                                  }}
                                  title={fieldValue}
                                >
                                  {fieldValue}
                                </a>
                              ) : isLookup && lookupReferenceObject && lookupId ? (
                                <a
                                  href={`/view/${lookupReferenceObject}/${lookupId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link-brand"
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    textDecoration: 'none',
                                    transition: theme.transitions.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.textDecoration = 'underline';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none';
                                  }}
                                  title={fieldValue}
                                >
                                  {fieldValue}
                                </a>
                              ) : (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={fieldValue && fieldValue !== '—' ? String(fieldValue) : ''}
                                >
                                  {fieldValue}
                                </span>
                              )}
                            </div>
                          </Col>
                        );
                      };

                      return (
                        <>
                          {/* For notes: Title/Name field - Full width row */}
                          {isNotesWidget && titleField && renderField(titleField, 12)}

                          {/* For notes: Description field - Full width row */}
                          {isNotesWidget && descriptionField && renderField(descriptionField, 12)}

                          {/* Other fields - 2 column grid (for all widgets, including title/description for non-notes) */}
                          {otherFields.map((fieldMeta, fieldIdx) => renderField(fieldMeta, 6))}
                        </>
                      );
                    })() : (
                      <Col xs={12}>
                        <p className="text-muted" style={{ fontSize: theme.typography.fontSize.lg }}>
                          No fields configured for this widget
                        </p>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
          {totalRecordsCount && totalRecordsCount > maxRecords && (
            <div className="text-center mt-2">
              <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                Showing {Math.min(recordsToRender.length, maxRecords)} of {totalRecordsCount} records
              </small>
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        <Card className="mb-3 widget-card" style={{ width: '100%', maxHeight: isCollapsed ? 'auto' : `${maxHeight}px`, display: 'flex', flexDirection: 'column' }}>
          <Card.Header
            className="widget-card-header"
            style={{
              padding: '12px 16px',
              borderBottom: isCollapsed ? 'none' : '1px solid #dee2e6',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}
          >
            {/* Title */}
            <h5
              className="mb-0"
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#ffffff',
                margin: 0,
                flex: '0 0 auto'
              }}
            >
              {widget.widgetLabel || 'Related List'}
            </h5>

            {/* Action Buttons and Arrow Container */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: '0 0 auto'
              }}
            >
              {/* Action Buttons - Before Arrow */}
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flex: '0 0 auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Create Record / Upload Button */}
                {targetObjectName && (targetObjectName.toLowerCase() === 'attachment' ||
                  widget.widgetLabel?.toLowerCase().includes('attachment')) ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAttachmentModal(true);
                    }}
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'rgb(255, 255, 255)',
                      margin: '0px',
                      flex: '0 0 auto',
                      padding: '4px 8px',
                      whiteSpace: 'nowrap',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Upload Attachment"
                  >
                    <i className="fas fa-upload" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                    Upload
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (targetObjectName) {
                        // Get target_field_name from widget data (now included by backend)
                        const targetFieldName = widget.data?.targetFieldName ||
                          widget.config?.targetFieldName ||
                          widget.config?.target_field_name;
                        // Store it in widget config for use in Create Modal
                        if (targetFieldName && !widget.config?.targetFieldName) {
                          widget.config = widget.config || {};
                          widget.config.targetFieldName = targetFieldName;
                        }
                        setShowCreateModal(true);
                      }
                    }}
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'rgb(255, 255, 255)',
                      margin: '0px',
                      flex: '0 0 auto',
                      padding: '4px 8px',
                      whiteSpace: 'nowrap',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Create New Record"
                  >
                    <i className="fas fa-plus" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                    Create Record
                  </button>
                )}
                {/* Show All Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!onShowAll || !targetObjectName) return;

                    // Get target_field_name from widget data (now included by backend)
                    // Priority: widget.data.targetFieldName > widget.config.targetFieldName > widget.config.target_field_name
                    const targetFieldName = widget.data?.targetFieldName ||
                      widget.config?.targetFieldName ||
                      widget.config?.target_field_name;

                    if (!targetFieldName) {
                      console.error('❌ Cannot show related records: target_field_name is missing from widget data');
                      alert('Error: Cannot determine relationship field. Please contact support.');
                      return;
                    }

                    // Get actions from widget data (from related list config) or widget config
                    const actions = widget.data?.actions ||
                      widget.config?.actions ||
                      ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export'];

                    // Get fields from widget config - ensure "id" is included for backend but will be filtered from display
                    let displayFields = widget.config?.fields || ['name'];
                    // Ensure "id" is included in fields sent to backend (it will be filtered out from display automatically)
                    if (!displayFields.includes('id')) {
                      displayFields = ['id', ...displayFields];
                    }

                    // Create a tab-like object similar to RelatedTabsModal structure
                    const tabObject = {
                      tabName: widget.widgetLabel || 'Related List',
                      config: {
                        target_object_name: targetObjectName,
                        target_field_name: targetFieldName,
                        actions: actions,
                        field_config: {
                          fields: displayFields
                        }
                      }
                    };

                    console.log('📋 Created tab object for Show All:', tabObject);
                    onShowAll(tabObject, currentRecordId, sourceObjectName);
                  }}
                  style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'rgb(255, 255, 255)',
                    margin: '0px',
                    flex: '0 0 auto',
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title="Show All Records"
                >
                  <i className="fas fa-list" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                  Show All
                </button>
              </div>

              {/* Collapse Arrow */}
              <i
                className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'rgb(255, 255, 255)',
                  margin: '0px',
                  flex: '0 0 auto',
                  transition: 'transform 0.2s ease',
                  cursor: 'pointer'
                }}
              />
            </div>
          </Card.Header>
          {!isCollapsed && (
            <Card.Body style={{ padding: '12px 16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {picklistField && picklistValues && picklistValues.length > 0 ? (
                <Tab.Container
                  id={`widget-tabs-${widget.widgetId}`}
                  activeKey={activeTab}
                  onSelect={handleTabSelect}
                >
                  <Nav variant="tabs" className="mb-3 widget-tabs-nav" style={{ borderBottom: '2px solid #dee2e6' }}>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="all"
                        style={{
                          color: activeTab === 'all' ? theme.colors.primary : '#495057',
                          fontWeight: activeTab === 'all' ? 600 : 500,
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          borderBottom: activeTab === 'all' ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
                          padding: '8px 16px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        All ({records.length})
                      </Nav.Link>
                    </Nav.Item>
                    {picklistValues.map((pv) => {
                      const count = actualGroupedRecords[pv.key]?.length || 0;
                      const isActive = activeTab === pv.key;
                      return (
                        <Nav.Item key={pv.key}>
                          <Nav.Link
                            eventKey={pv.key}
                            style={{
                              color: isActive ? theme.colors.primary : '#495057',
                              fontWeight: isActive ? 600 : 500,
                              cursor: 'pointer',
                              backgroundColor: 'transparent',
                              borderBottom: isActive ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
                              padding: '8px 16px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {pv.label} ({count})
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                  </Nav>
                  <Tab.Content className="widget-tab-content" style={{ minHeight: '200px', paddingTop: '10px' }}>
                    <Tab.Pane
                      eventKey="all"
                      transition={false}
                      className={activeTab === 'all' ? 'active' : ''}
                      data-tab-name="all"
                      data-is-active={activeTab === 'all'}
                    >
                      {(() => {
                        const allRecords = actualGroupedRecords['all'] || [];
                        console.log('All tab - Rendering with records:', allRecords.length, 'activeTab:', activeTab, 'className:', activeTab === 'all' ? 'active' : '');
                        if (allRecords.length === 0) {
                          return <p className="text-muted">No records in All tab</p>;
                        }
                        return renderRecordCards(allRecords, records.length);
                      })()}
                    </Tab.Pane>
                    {picklistValues.map((pv) => {
                      const tabRecords = actualGroupedRecords[pv.key] || [];
                      const totalForTab = records.filter(r => {
                        const normalized = normalizePicklistValue(r[picklistField]);
                        return normalized !== null && String(normalized) === String(pv.key);
                      }).length;
                      const isActive = activeTab === pv.key;
                      console.log(`Tab ${pv.key} - Rendering with records:`, tabRecords.length, 'of', totalForTab, 'activeTab:', activeTab, 'isActive:', isActive, 'className:', isActive ? 'active' : '');
                      return (
                        <Tab.Pane
                          key={pv.key}
                          eventKey={pv.key}
                          transition={false}
                          className={isActive ? 'active' : ''}
                          data-tab-name={pv.key}
                          data-is-active={isActive}
                        >
                          {tabRecords.length === 0 ? (
                            <p className="text-muted">No records in {pv.label} tab</p>
                          ) : (
                            renderRecordCards(tabRecords, totalForTab)
                          )}
                        </Tab.Pane>
                      );
                    })}
                  </Tab.Content>
                </Tab.Container>
              ) : (
                renderRecordCards(displayRecords)
              )}
            </Card.Body>
          )}
        </Card>

        {/* Edit Modal */}
        {showEditModal && editingRecord && targetObjectName && (
          <EditLayoutModal
            show={showEditModal}
            onHide={handleModalCancel}
            objectName={targetObjectName}
            id={editingRecord.id?.value}
            sourcePage="detail"
            onSave={handleModalSaveSuccess}
            onCancel={handleModalCancel}
            onRefresh={onRefresh}
          />
        )}

        {/* Clone Modal */}
        {showCloneModal && editingRecord && targetObjectName && (
          <EditLayoutModal
            show={showCloneModal}
            onHide={handleModalCancel}
            objectName={targetObjectName}
            id={editingRecord.id?.value}
            isClone={true}
            sourcePage="detail"
            onSave={handleModalSaveSuccess}
            onCancel={handleModalCancel}
            onRefresh={onRefresh}
          />
        )}

        {/* Create Modal */}
        {showCreateModal && targetObjectName && (
          <EditLayoutModal
            show={showCreateModal}
            onHide={() => {
              setShowCreateModal(false);
            }}
            objectName={targetObjectName}
            id={null}
            sourcePage="detail"
            onSave={(savedData) => {
              setShowCreateModal(false);
              // Refresh the widget data
              if (onRefresh) {
                onRefresh();
              }
            }}
            onCancel={() => {
              setShowCreateModal(false);
            }}
            onRefresh={onRefresh}
            queryParams={
              currentRecordId && sourceObjectName
                ? {
                  targetField: widget.data?.targetFieldName || widget.config?.targetFieldName || widget.config?.target_field_name,
                  targetId: currentRecordId,
                  parentObjectName: sourceObjectName
                }
                : undefined
            }
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setRecordToDelete(null);
          }}
          onConfirm={confirmDeleteRecord}
          title="Confirm Delete"
          message="Are you sure you want to delete this record?"
          itemName={recordToDelete ? (getRecordIdValue(recordToDelete) || 'this record') : 'this record'}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />

        {/* Attachment Upload Modal */}
        {showAttachmentModal && (
          <Attachment
            parentId={currentRecordId}
            onUploadSuccess={uploadedFiles => {
              console.log('Files uploaded successfully:', uploadedFiles);
              // Refresh the widget data to show new files
              if (onRefresh) {
                onRefresh();
              }
              setShowAttachmentModal(false);
            }}
            onClose={() => setShowAttachmentModal(false)}
          />
        )}

        {/* File Preview Modal */}
        {showPreviewModal && previewFileRecord && (
          <FilePreviewModal
            show={showPreviewModal}
            onHide={() => {
              setShowPreviewModal(false);
              setPreviewFileRecord(null);
            }}
            fileRecord={previewFileRecord}
          />
        )}
      </>
    );
  }


  // For other widget types, render placeholder
  return (
    <Card className="mb-3 widget-card" style={{ width: '100%' }}>
      <Card.Header
        className="widget-card-header"
        style={{
          padding: '12px 16px',
          borderBottom: isOtherCollapsed ? 'none' : '1px solid #dee2e6',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        onClick={() => setIsOtherCollapsed(!isOtherCollapsed)}
      >
        <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600, color: theme.colors.primary }}>
          {widget.widgetLabel || 'Widget'}
        </h5>
        <i
          className={`fas fa-chevron-${isOtherCollapsed ? 'down' : 'up'}`}
          style={{
            color: theme.colors.textSecondary,
            fontSize: '0.875rem',
            transition: 'transform 0.2s ease'
          }}
        />
      </Card.Header>
      {!isOtherCollapsed && (
        <Card.Body style={{ padding: '12px 16px' }}>
          <p className="text-muted" style={{ fontSize: theme.typography.fontSize.lg }}>
            Widget type: {widget.widgetType}
          </p>
        </Card.Body>
      )}
    </Card>
  );
};

export default LayoutWidget;
